import { forwardRef } from "react";
import type { ButtonHTMLAttributes } from "react";
import clsx from "clsx";
import { Spinner } from "../Spinner/Spinner.js";
import styles from "./Button.module.scss";

/**
 * Button variants. `primary` is INK/inverse (BRAND-3: crimson is never a primary button in the
 * band tool — the exceptions are Antenna's fan CTA and, from 2026-08-23, the AUTH screens); `accent`
 * is crimson for accent-only usage;
 *
 * The auth exception is an owner ruling on STYLE-GUIDE open delta 1 ("primary buttons are ink, not
 * crimson"), which recorded that §11, the CD specs AND the legacy stylesheet all want crimson and only
 * ours does not. The delta warned that a GLOBAL flip breaks §15 ("one primary CTA per view") across
 * ~253 sites; the auth screens are the safe scope because each carries exactly one CTA. Do not widen
 * this to other surfaces without the per-screen pass the delta asks for.
 * `ghost` is paper + line-strong; `dashed` is the "+ Add <thing>" affordance.
 */
export type ButtonVariant = "primary" | "ghost" | "accent" | "link" | "dashed";
export type ButtonSize = "xs" | "sm" | "md" | "field";

/**
 * `danger` tints the HOVER for a destructive action — the same contract `IconButton` has carried since it
 * was extracted, brought across so a text button that deletes something does not have to fork a local class
 * to say so (the show preview's footer `Delete`, PO 2026-08-26).
 *
 * Hover only, deliberately: a bar of actions where the destructive one is already red at rest reads as an
 * error state, and it pulls the eye to the one thing nobody opened the dialog to do. The colour is the
 * answer to "what happens if I press this", which is a question the pointer has already asked.
 */
export type ButtonTone = "default" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /**
   * `md` 44px · `sm` 36px · **`xs` 30px — DESKTOP ONLY** · `field` = `--ac-control-h`.
   *
   * `field` is the only rung that is not a fixed number: it reads the SAME token `Field`'s `.control`
   * reads, so a button standing next to an input is exactly as tall as the input at every breakpoint
   * (44px touch, 28px fine pointer). None of the fixed rungs can do that — 30/36/44 against a 28px box is
   * 2/8/16px of mismatch, and the pair stops reading as one control. It is also the one small rung that is
   * mobile-SAFE, because the token is 44px there.
   *
   * `xs` is deliberately under the guide's §14 minimum ("UI controls minimum touch target: 44×44pt on
   * iOS"). It exists for dense desktop chrome — card-header save pills, row controls sized to sit
   * beside a picker — where 19 hand-rolled pills had grown up because no rung was small enough.
   * **Never use it on a mobile surface** (`shells/mobile/*`, `features/mobile/*`, or any `*Mobile*`
   * screen); a guard test fails the build if it appears there. Reach for `sm` or larger on phones.
   */
  size?: ButtonSize;
  /** ADDITIVE loading state: a width-stable spinner-in-pill; the button is disabled while loading.*/
  loading?: boolean;
  /** `danger` tints the hover for destructive actions. See {@link ButtonTone}. */
  tone?: ButtonTone;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", tone = "default", loading = false, fullWidth = false, disabled, children, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-tone={tone === "default" ? undefined : tone}
      className={clsx(
        styles.button,
        styles[variant],
        styles[size],
        fullWidth && styles.fullWidth,
        loading && styles.loading,
        className,
      )}
      {...rest}
    >
      {loading && <Spinner size={14} className={styles.spinner} />}
      <span className={styles.label}>{children}</span>
    </button>
  );
});
